/**
 * Cross-domain GA4 attribution helpers.
 *
 * Why this exists
 * ----------------
 * Visitors arrive from a Meta ad on whitesmileclinic.ie, hop to
 * book.whitesmileclinic.ie, complete a booking, and are redirected back to
 * whitesmileclinic.ie/bookly-confirmation-thank-you/ — where a pre-existing
 * `book_online` GA4 event fires. For that event to be attributed to the
 * originating Meta campaign in GA4 we rely on GA4's cross-domain linker:
 * the `_ga` client_id and `_ga_<ID>` session_id follow the user across
 * sub-domains via the `_gl` URL parameter.
 *
 * The gtag auto-linker (configured in public/index.html) handles that
 * automatically for anchor clicks and form submits, and this module reuses
 * the same mechanism for the programmatic `window.location.href` redirects
 * the wizard uses after payment / booking submission — by synthesising an
 * anchor click that the linker intercepts.
 *
 * Acquisition params (utm_*, fbclid, gclid, …) are intentionally NOT
 * forwarded onto the thank-you URL. The original acquisition should
 * identify the visitor's entry only; re-introducing it later in the
 * journey would corrupt attribution. We still snapshot the entry params
 * into sessionStorage so they can be read for internal attribution or
 * debugging without ever leaking back into GA4 as a fresh acquisition.
 *
 * No PII is ever stored or sent — only standard campaign params.
 */

const FORWARD_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "utm_id", "gclid", "fbclid", "gbraid", "wbraid", "msclkid",
];

const STORAGE_KEY = "ws_ga_entry_params";

/** Call once on app mount — snapshots any UTM/click-id params from the entry
 *  URL into sessionStorage for internal attribution/debugging. These values
 *  are NEVER re-injected into outbound URLs. */
export function captureEntryParams() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const captured = {};
    for (const p of FORWARD_PARAMS) {
      const v = url.searchParams.get(p);
      if (v) captured[p] = v;
    }
    if (Object.keys(captured).length && !sessionStorage.getItem(STORAGE_KEY)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    }
  } catch {
    /* URL / sessionStorage unavailable — safe to ignore. */
  }
}

/**
 * Navigate to `url` while letting the gtag auto-linker attach `_gl`
 * (GA4 client_id + session_id) to the href — we do that by synthesising
 * a real anchor click, which the gtag linker intercepts the same way it
 * would a real user click.
 *
 * Acquisition parameters (utm_*, fbclid, gclid …) captured from the entry
 * URL are deliberately NOT appended — see module header.
 *
 * Falls back to a plain `window.location.href` navigation if gtag is not
 * ready (ad blocker, offline, etc.). GA4 session continuity may be lost
 * in that case, but booking functionality is unaffected.
 */
export function navigateWithAttribution(url) {
  if (!url || typeof window === "undefined") return;

  // Use a synthetic anchor click so gtag's cross-domain linker runs and
  // appends `_gl=...` before the browser follows the href. This mirrors
  // exactly what GA4 does for real user clicks on <a href>.
  if (typeof document !== "undefined" && typeof window.gtag === "function") {
    try {
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Safety net — if the synthetic click didn't trigger navigation for
      // any reason within 400 ms, force the redirect so the booking flow
      // never leaves the patient staring at a spinner.
      setTimeout(() => {
        if (window.location.href !== url) {
          window.location.href = url;
        }
      }, 400);
      return;
    } catch {
      /* fall through to plain redirect */
    }
  }
  window.location.href = url;
}
